import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowRight, Target, Layers, Shuffle, Award, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Alert, AlertDescription } from "../components/ui/alert";

const MODES = [
  { id: "by_topic", title: "Por tema", icon: Target, desc: "Selecciona temas concretos." },
  { id: "by_block", title: "Por bloques", icon: Layers, desc: "Un bloque completo del temario." },
  { id: "random", title: "Aleatorio", icon: Shuffle, desc: "Preguntas mezcladas de toda la categoría." },
  { id: "simulation", title: "Simulacro", icon: Award, desc: "Examen completo con tiempo limitado." },
];

export default function TestConfig() {
  const { categoryId } = useParams();
  const location = useLocation();
  const isFailedMode = location.pathname === "/tests/failed";
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [mode, setMode] = useState(isFailedMode ? "failed" : "random");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [num, setNum] = useState([20]);
  const [duration, setDuration] = useState([30]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [failedCount, setFailedCount] = useState(null);

  useEffect(() => {
    if (isFailedMode) {
      api.get("/failed-questions/count").then((r) => setFailedCount(r.data)).catch(console.error);
    } else if (categoryId) {
      api.get(`/categories/${categoryId}`).then((r) => {
        setCat(r.data);
        if (r.data.blocks?.length) setSelectedBlock(r.data.blocks[0].id);
      }).catch(console.error);
    }
  }, [categoryId, isFailedMode]);

  const availableModes = MODES.filter((m) => m.id !== "by_block" || (cat?.blocks?.length || 0) > 0);
  const toggleTopic = (id) => setSelectedTopics((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const onStart = async () => {
    setErr("");
    if (mode === "by_topic" && selectedTopics.length === 0) { setErr("Selecciona al menos un tema."); return; }
    setLoading(true);
    try {
      const payload = {
        category_id: isFailedMode ? null : categoryId,
        mode,
        num_questions: num[0],
        topic_ids: mode === "by_topic" ? selectedTopics : null,
        block_id: mode === "by_block" ? selectedBlock : null,
        duration_minutes: mode === "simulation" ? duration[0] : null,
      };
      const { data } = await api.post("/tests/start", payload);
      navigate(`/tests/run/${data.test_id}`, { state: { test: data } });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  // Failed mode
  if (isFailedMode) {
    return (
      <div className="min-h-screen bg-ink relative">
        <SEO title="Preguntas falladas" path="/tests/failed" />
        <div className="fixed inset-0 bg-aurora pointer-events-none" />
        <div className="relative">
          <Header />
          <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">Repaso</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[0.95]" data-testid="failed-mode-title">
              Preguntas que<br /><span className="text-rose-400">has fallado.</span>
            </h1>
            <p className="mt-3 text-base text-slate-400">Refuerza tus puntos débiles repasando los errores anteriores.</p>

            {err && <Alert variant="destructive" className="mt-6 border-red-900/50 bg-red-950/40 text-red-200"><AlertDescription>{err}</AlertDescription></Alert>}

            <div className="mt-10 bg-ink-800 border border-white/5 rounded-3xl p-7">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Total disponibles</div>
                  <div className="font-display text-6xl lg:text-7xl font-extrabold text-white mt-1 tracking-tighter">{failedCount?.total ?? "—"}</div>
                </div>
                <RotateCcw className="h-10 w-10 text-gcgold" strokeWidth={1.5} />
              </div>

              {failedCount && failedCount.total === 0 && (
                <div className="mt-6 p-4 bg-emerald-950/30 border border-emerald-700/30 rounded-xl text-sm text-emerald-300">
                  ¡Increíble! No tienes preguntas falladas registradas. Realiza tests para que aparezcan aquí.
                </div>
              )}

              {failedCount && failedCount.total > 0 && (
                <>
                  {failedCount.by_category?.length > 0 && (
                    <div className="mt-6">
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3 font-mono">Por categoría</div>
                      <div className="space-y-2">
                        {failedCount.by_category.map((c) => (
                          <div key={c.category_id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5 rounded-xl">
                            <span className="text-sm text-slate-300">{c.category_name}</span>
                            <span className="font-mono text-sm text-gcgold font-bold">{c.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-400">Número de preguntas</span>
                      <span className="font-display text-xl font-extrabold text-gcgold">{Math.min(num[0], failedCount.total)}</span>
                    </div>
                    <Slider min={5} max={Math.min(100, Math.max(5, failedCount.total))} step={5} value={num} onValueChange={setNum} data-testid="failed-num-slider" />
                  </div>

                  <Button onClick={onStart} disabled={loading}
                    className="mt-6 w-full bg-gcgold hover:bg-gcgold-light text-ink font-bold h-12 rounded-xl shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] group"
                    data-testid="start-failed-test">
                    {loading ? "Preparando..." : `Empezar repaso (${Math.min(num[0], failedCount.total)} preguntas)`} <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </>
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>
    );
  }

  if (!cat) {
    return (
      <div className="min-h-screen bg-ink">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-500">Cargando...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title={`Test de ${cat.name}`} path={`/tests/${cat.id}`} />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />

      <div className="relative">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono">{cat.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[0.95]" data-testid="test-config-title">
            Configura tu test
          </h1>
          <p className="mt-3 text-base text-slate-400">{cat.description}</p>

          {err && <Alert variant="destructive" className="mt-6 border-red-900/50 bg-red-950/40 text-red-200" data-testid="config-error"><AlertDescription>{err}</AlertDescription></Alert>}

          {/* Mode selection */}
          <section className="mt-8 bg-ink-800 border border-white/5 rounded-2xl p-6">
            <h2 className="font-display text-base font-bold text-white mb-5 flex items-center gap-2">
              <span className="text-gcgold font-mono text-xs">01.</span> Modo
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mode-options">
              {availableModes.map((m) => {
                const Icon = m.icon;
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMode(m.id)}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      active ? "border-gcgold/50 bg-gcgold/5" : "border-white/5 hover:border-white/10 bg-white/[0.02]"
                    }`}
                    data-testid={`mode-${m.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${active ? "bg-gcgold/20 text-gcgold" : "bg-white/5 text-slate-400"}`}>
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <div className="font-display font-bold text-white">{m.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{m.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Topics */}
          {mode === "by_topic" && (
            <section className="mt-4 bg-ink-800 border border-white/5 rounded-2xl p-6" data-testid="topic-section">
              <h2 className="font-display text-base font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-gcgold font-mono text-xs">02.</span> Temas
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {cat.topics.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 hover:border-white/10 cursor-pointer bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                    <Checkbox checked={selectedTopics.includes(t.id)} onCheckedChange={() => toggleTopic(t.id)} data-testid={`topic-checkbox-${t.id}`} className="border-white/20 data-[state=checked]:bg-gcgold data-[state=checked]:border-gcgold data-[state=checked]:text-ink" />
                    <div className="flex-1 text-sm text-slate-300">{t.name}</div>
                    <span className="text-xs font-mono text-slate-500">{t.question_count}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Blocks */}
          {mode === "by_block" && cat.blocks?.length > 0 && (
            <section className="mt-4 bg-ink-800 border border-white/5 rounded-2xl p-6">
              <h2 className="font-display text-base font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-gcgold font-mono text-xs">02.</span> Bloque
              </h2>
              <RadioGroup value={selectedBlock} onValueChange={setSelectedBlock} className="space-y-2">
                {cat.blocks.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                    <RadioGroupItem value={b.id} id={b.id} data-testid={`block-radio-${b.id}`} className="border-white/20 text-gcgold" />
                    <Label htmlFor={b.id} className="flex-1 cursor-pointer text-sm text-slate-300">{b.name}</Label>
                  </div>
                ))}
              </RadioGroup>
            </section>
          )}

          {/* Num & duration */}
          <section className="mt-4 bg-ink-800 border border-white/5 rounded-2xl p-6">
            <h2 className="font-display text-base font-bold text-white mb-5 flex items-center gap-2">
              <span className="text-gcgold font-mono text-xs">{mode === "by_topic" || mode === "by_block" ? "03." : "02."}</span> Preguntas
            </h2>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">Número de preguntas</span>
              <span className="font-display text-xl font-extrabold text-gcgold tracking-tighter">{num[0]}</span>
            </div>
            <Slider min={5} max={100} step={5} value={num} onValueChange={setNum} data-testid="num-questions-slider" />

            {mode === "simulation" && (
              <>
                <div className="flex items-center justify-between mt-6 mb-3">
                  <span className="text-sm text-slate-400">Duración (minutos)</span>
                  <span className="font-display text-xl font-extrabold text-gcgold tracking-tighter">{duration[0]} min</span>
                </div>
                <Slider min={10} max={120} step={5} value={duration} onValueChange={setDuration} data-testid="duration-slider" />
              </>
            )}
          </section>

          <Button onClick={onStart} disabled={loading}
            className="mt-8 w-full sm:w-auto bg-gcgold hover:bg-gcgold-light text-ink font-bold h-12 px-8 rounded-xl shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)] group"
            data-testid="start-test-button">
            {loading ? "Preparando..." : "Comenzar test"} <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </main>
        <Footer />
      </div>
    </div>
  );
}
